import React, { useEffect, useContext } from 'react';
import { observer } from 'mobx-react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@emotion/react';
import dayjs from 'dayjs';
import dayjsRelativeTime from 'dayjs/plugin/relativeTime';
import dayjsDuration from 'dayjs/plugin/duration';
import dayjsUtc from 'dayjs/plugin/utc';
import themes from '~/themes';
import {
  Home,
  Login,
  Videos,
  VideoEdit,
  ChapterEditor,
  Visibility,
  CoordsEditor,
  NotFound,
  TermsConditions,
} from '~/routes';
import ErrorBoundary from '@components/Error/ErrorBoundary';
import ContainerSkeleton from '@components/ContainerSkeleton';
import ReactGA from 'react-ga';
import { notification, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { UploaderContext } from '~/lib/video-uploader';
import AuthRoute from '~/routes/AuthRoute';
import { IUserStore } from '@stores/userStore';
import { useAuthStore, useAuthenticated, AuthState, useInitialized } from '@stores/auth';
import { useUIStore } from '@stores/ui';
import useStores from '@lib/hooks/useStores';
import { ReactComponent as IconDoneSuccess } from '@svg/common/icon-done-success.svg';
import { ReactComponent as IconVideos } from '@svg/icon-videos.svg';
import { ReactComponent as IconPublishedPublic } from '@svg/icon-published-public.svg';

import '@lib/ga';

// Day.js Locales
import 'dayjs/locale/en';
import 'dayjs/locale/ko';
import Container from '@components/Container';

dayjs.extend(dayjsRelativeTime);
dayjs.extend(dayjsDuration);
dayjs.extend(dayjsUtc);

const App: React.FC = observer(() => {
  const { userStore } = useStores<{
    userStore: IUserStore;
  }>();
  const { authState } = useAuthStore();
  const isInitialized = useInitialized();
  const isAuthenticated = useAuthenticated();
  const { currentTheme, overrideTheme, isGlobalLoading } = useUIStore();
  // const { isLoading, isAuthenticated } = userStore;
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const videoUploader = useContext(UploaderContext);

  useEffect(() => {
    userStore.initialize();
    dayjs.locale(i18n.languages[0]);
    // [#30]
    // Listener for Prefers color scheme when user set 'auto'
    // if (theme === 'auto' && window.matchMedia) {
    //   if ('addEventListener' in window.matchMedia('(prefers-color-scheme: dark)')) {
    //     window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    //       setTheme('auto');
    //     });
    //   } else {
    //     window.matchMedia('(prefers-color-scheme: dark)').addListener(() => {
    //       setTheme('auto');
    //     });
    //   }
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.languages, userStore]);

  // Observing Uploading videos
  useEffect(() => {
    function beforeUnloadHandler(e: BeforeUnloadEvent) {
      if (userStore.isUploadingVideo) e.returnValue = '';

      return '';
    }

    if (userStore.isUploadingVideo) {
      window.onbeforeunload = beforeUnloadHandler;
      window.onunload = beforeUnloadHandler;
    } else {
      window.onbeforeunload = null;
      window.onunload = null;
    }
  }, [userStore.isUploadingVideo, t]);

  useEffect(() => {
    if (userStore.completedUploadingVideoIds.length) {
      userStore.completedUploadingVideoIds.forEach((id: string) => {
        if (id in userStore.currentlyUploadingVideoDatas) {
          const videoId = userStore.currentlyUploadingVideoDatas[id].videoId;
          const videoTitle = userStore.currentlyUploadingVideoDatas[id].title;

          if (location.pathname !== `/video/${videoId}/edit`) {
            notification.success({
              key: `complete-uploading-${id}`,
              message: t('header.action.upload.notification.message'),
              description: t('header.action.upload.notification.descsription', { videoTitle }),
              top: 64,
              style: {
                marginTop: 24,
              },
              icon: <IconDoneSuccess />,
              btn: (
                <Button
                  type="primary"
                  size="large"
                  icon={<IconVideos style={{ marginRight: 8, verticalAlign: 'top' }} />}
                  style={{ backgroundColor: '#52C41A', borderColor: '#52C41A', color: '#090909' }}
                  onClick={() => {
                    notification.close(`complete-uploading-${id}`);
                    navigate(`/video/${videoId}/edit`);
                    videoUploader.cancelVideoUpload(id);
                  }}
                >
                  {t('common.button.routing.video').toString()}
                </Button>
              ),
              onClose() {
                notification.close(`complete-uploading-${id}`);
                videoUploader.cancelVideoUpload(id);
              },
              duration: 0,
            });
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStore.completedUploadingVideoIds.length]);

  // Observing when video is complete to processing.
  useEffect(() => {
    if (userStore.completedProcessingVideoIds.length) {
      userStore.completedProcessingVideoIds.forEach((id: string) => {
        const videoTitle = userStore.currentlyProcessingVideoDatas[id].title;
        notification.success({
          key: `complete-estimation-${id}`,
          message: t('header.action.progress.notification.message'),
          description: t('header.action.progress.notification.descsription', { videoTitle }),
          // message: t('header.action.progress.chapter.complete.message'),
          // description: t('header.action.progress.chapter.complete.description', { videoTitle }),
          top: 64,
          style: {
            marginTop: 24,
          },
          btn: (
            <Button
              type="primary"
              size="large"
              icon={
                <IconPublishedPublic
                  width="20"
                  height="20"
                  style={{
                    marginRight: 8,
                    marginTop: 1,
                    verticalAlign: 'top',
                    filter: 'brightness(0)',
                  }}
                />
              }
              style={{ backgroundColor: '#52C41A', borderColor: '#52C41A', color: '#090909' }}
              onClick={() => {
                notification.close(`complete-estimation-${id}`);
                navigate(`/video/${id}/visibility`);
                userStore.ejectProcessingVideo(id);
              }}
            >
              {t('common.button.routing.visibility').toString()}
            </Button>
          ),
          duration: 0,
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStore.completedProcessingVideoIds.length]);

  // Google Analytics
  useEffect(() => ReactGA.pageview(window.location.pathname + window.location.search), [location]);

  return (
    <ThemeProvider theme={themes[overrideTheme ?? currentTheme]}>
      <ErrorBoundary>
        {isInitialized &&
          (authState === AuthState.FETCHING_USER ? (
            <ContainerSkeleton />
          ) : isAuthenticated ? (
            <Routes>
              <Route element={<AuthRoute />}>
                <Route element={<Container type="default" />}>
                  <Route index element={<Home />} />
                  <Route path="/videos" element={<Videos />} />
                </Route>
                <Route element={<Container type="sidebarless" />}>
                  <Route path="/terms" element={<TermsConditions />} />
                </Route>
                <Route element={<Container type="video" />}>
                  <Route path="/video/:videoId/edit" element={<VideoEdit />} />
                  <Route path="/video/:videoId/editor/chapter" element={<ChapterEditor />} />
                  <Route path="/video/:videoId/visibility" element={<Visibility />} />
                  <Route path="/video/:videoId/editor/coords" element={<CoordsEditor />} />
                </Route>
                <Route path="/logout" element={<Navigate replace to="/" />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          ) : (
            <Routes>
              <Route path="/" element={<Login />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          ))}
        {isGlobalLoading && <div className="global-loading-dimmer"></div>}
      </ErrorBoundary>
    </ThemeProvider>
  );
});

export default App;
